import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAnnouncements, getCurrentUser, getProfile, createAnnouncement } from '../utils/supabase';
import ReactMarkdown from 'react-markdown';

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
  });

  const isAdmin = profile?.is_admin || false;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { user } = await getCurrentUser();
    setUser(user);
    if (user) {
      const { data } = await getProfile(user.id);
      setProfile(data);
    }
    const { data } = await getAnnouncements();
    setAnnouncements(data || []);
    setLoading(false);
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Please sign in to create announcements');
      return;
    }
    if (!isAdmin) {
      setError('Only admins can create announcements');
      return;
    }

    setSubmitting(true);
    setError('');

    const announcementData = {
      title: newAnnouncement.title,
      content: newAnnouncement.content,
      created_by: user.id,
    };

    const { data, error } = await createAnnouncement(announcementData);
    if (error) {
      setError(error.message);
      setSubmitting(false);
      return;
    }

    setAnnouncements(prev => [{
      ...data,
      profiles: {
        id: profile.id,
        full_name: profile.full_name,
        avatar_url: profile.avatar_url,
      }
    }, ...prev]);

    setNewAnnouncement({ title: '', content: '' });
    setShowForm(false);
    setSubmitting(false);
  };

  const truncateContent = (content, maxLength = 200) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] pb-24">
      <div className="container-custom py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">📢 Announcements</h1>
            <p className="text-gray-500 text-sm">
              {announcements.length} announcements
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              {showForm ? 'Cancel' : '+ New Announcement'}
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
            <button onClick={() => setError('')} className="ml-2">✕</button>
          </div>
        )}

        {/* Create Announcement Form */}
        {showForm && isAdmin && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6 animate-fade-in">
            <form onSubmit={handleCreateAnnouncement}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    placeholder="Announcement title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Content (Markdown supported)
                  </label>
                  <textarea
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    placeholder="Write your announcement here... (Markdown supported)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent min-h-[150px]"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    You can use Markdown for formatting (e.g., **bold**, *italic*, [links](url), etc.)
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {submitting ? 'Publishing...' : 'Publish Announcement'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Announcements List */}
        <div className="space-y-4">
          {announcements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📢</div>
              <p>No announcements yet</p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <Link
                key={announcement.id}
                to={`/announcements/${announcement.id}`}
                className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold mb-2">{announcement.title}</h2>
                    <div className="text-gray-600 text-sm line-clamp-3">
                      <ReactMarkdown>
                        {truncateContent(announcement.content)}
                      </ReactMarkdown>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-400">
                      <span>
                        By {announcement.profiles?.full_name || 'Unknown'}
                      </span>
                      <span>•</span>
                      <span>
                        {new Date(announcement.created_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      <span>•</span>
                      <span className="text-primary">
                        Read more →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Announcement;
